import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import {
  SecurityTransitionOverlay,
  SecurityTransitionProgress,
  SecurityTransitionRetryButton,
  SecurityTransitionSeal,
} from './styled'

const MINIMUM_VISIBILITY_MS = 1500

interface ISecurityTransitionProps {
  errorMessage?: string | null
  isLoading: boolean
  isMfa?: boolean
  isPersistent?: boolean
  onRetry?: () => void
  children?: ReactNode
}

const SecurityTransition: FC<ISecurityTransitionProps> = ({
  errorMessage = null,
  isLoading,
  isMfa = false,
  isPersistent = false,
  onRetry,
  children,
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const startedAtRef = useRef<number>(0)
  const hasError = Boolean(errorMessage)

  useEffect(() => {
    if (isLoading || hasError) {
      if (!isVisible) {
        startedAtRef.current = Date.now()
        setIsVisible(true)
      }
      return
    }

    if (isPersistent || !isVisible) {
      return
    }

    const elapsedMs = Date.now() - startedAtRef.current
    const hideAfterMs = Math.max(0, MINIMUM_VISIBILITY_MS - elapsedMs)
    const timeoutId = window.setTimeout(() => setIsVisible(false), hideAfterMs)

    return () => window.clearTimeout(timeoutId)
  }, [hasError, isLoading, isPersistent, isVisible])

  if (!isPersistent && !isLoading && !hasError && !isVisible) {
    return null
  }

  if (isPersistent && !isLoading && !hasError && children) {
    return <SecurityTransitionOverlay persistent>{children}</SecurityTransitionOverlay>
  }

  let title = 'Protegiendo tu acceso'
  let description = 'Validamos tus credenciales y preparamos una conexión segura.'

  if (hasError) {
    title = 'No hemos podido validar el acceso'
    description = errorMessage || 'No hemos podido completar el acceso. Inténtalo nuevamente.'
  } else if (isMfa) {
    title = 'Validando tu segundo factor'
    description = 'Comprobamos tu código y preparamos una conexión segura.'
  }

  return (
    <SecurityTransitionOverlay
      persistent={isPersistent}
      aria-live={hasError ? 'assertive' : 'polite'}
      role={hasError ? 'alert' : 'status'}
    >
      <SecurityTransitionSeal aria-hidden="true">{hasError ? '!' : '✓'}</SecurityTransitionSeal>
      <h2>{title}</h2>
      <p>{description}</p>
      {hasError ? (
        <SecurityTransitionRetryButton type="button" onClick={onRetry}>
          Volver a intentar
        </SecurityTransitionRetryButton>
      ) : (
        <>
          <SecurityTransitionProgress aria-label="Validación de acceso en curso" role="progressbar" />
          <span>Un instante, por favor.</span>
        </>
      )}
    </SecurityTransitionOverlay>
  )
}

export default SecurityTransition
